<template>
  <v-card
    v-if="canShowBlocks()"
    elevation="0"
    class="v-card"
    :class="config.mainClasses || ''"
  >
    <v-card-title
      v-if="config.title || config.buttons"
      :class="config.cardTitle || ''"
    >
     <v-row>
<!--       Title-->
       <v-col
         class="title"
         :class="config.titleClasses || ''"
       >
         {{ config.title || '' }}
       </v-col>

<!--       Buttons-->
       <v-col
         v-if="config.buttons && config.buttons.length"
         class="d-flex justify-end"
       >
         <universal-button
           v-for="(button, buttonIndex) in config.buttons"
           :key="buttonIndex"
           :type="button.type || 'base'"
           :label="button.label || ''"
           :color="button.color || undefined"
           :class="button.class || ''"
           :style="button.style || {}"
           :prepend-icon="button.prependIcon || ''"
           :append-icon="button.appendIcon || ''"
           :prepend-icon-style="button.prependIconStyle || {}"
           @click="onButtonClick(button)"
         />
       </v-col>
     </v-row>
    </v-card-title>

    <v-card-text
      :class="getBlocksWrapperClasses()"
    >
      <v-overlay
        v-if="showLoader"
        style=""
        opacity="0.2"
        absolute
      >
        <v-progress-circular
          indeterminate
          :color="'var(--v-primary-base)'"
          size="60"
        />
      </v-overlay>
      <template
        v-if="formDataCopy[config.dataKey] && formDataCopy[config.dataKey].length"
      >
        <v-row
          v-for="(row, rowIndex) in formDataCopy[config.dataKey]"
          :key="rowIndex"
          class="blocks-row"
          :class="row.class || ''"
        >
          <template
            v-for="(block, blockIndex) in config.columnsConfig"
          >
            <v-col
              v-if="canShowBlock(block, rowIndex)"
              :key="blockIndex"
              class="block-col"
              :class="getBlockClasses(block)"
              :style="getBlockStyle(block)"
              @click="onBlockClick(block, rowIndex)"
            >
              {{ block.title || '-' }}
              <br
                v-if="block.newLine === true || block.newLine === undefined"
              >
              <div
                :style="block.contentWrapperStyle || {}"
              >
                <!--              Prepend block value icon-->
                <v-icon
                  v-if="block.prependIcon && block.prependIcon.icon"
                  :style="block.prependIcon.style || {}"
                >
                  {{ block.prependIcon.icon }}
                </v-icon>

                <!--              Prepend block value image-->
                <v-img
                  v-if="block.prependImage && block.prependImage.src"
                  :style="block.prependImage.style || {}"
                  :src="block.prependImage.src"
                  :width="block.prependImage.width || defaultPrependImageWidth"
                  :height="block.prependImage.height || defaultPrependImageHeight"
                />

                <!--              Block value-->
                <div
                  class="block-value"
                  :class="block.valueClass || ''"
                >
                  {{ getBlockValue(block, rowIndex) }}
                </div>
              </div>

              <v-img
                v-if="block.deletable === true"
                src="/img/icons/delete-file-icon.svg"
                class="delete-image"
                width="30"
                height="30"
                @click="onSingleBlockDelete(block, rowIndex, blockIndex)"
              />
            </v-col>
          </template>
        </v-row>
      </template>

      <template
        v-else
      >
        <v-row>
          <v-col
            class="no-data-text"
          >
            {{ $t('base.no_data') }}
          </v-col>
        </v-row>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import { cloneDeep, isEmpty, isObject } from 'lodash'
import UniversalButton from '@/global/components/buttons/UniversalButton.vue'

const DEFAULT_BLOCK_VALUE = '-'
const DEFAULT_PREPEND_IMAGE_WIDTH = 20
const DEFAULT_PREPEND_IMAGE_HEIGHT = 20
export default {
  name: 'RowBlocksData',

  components: {
    UniversalButton
  },

  props: {
    config: {
      type: Object,
      default: () => ({})
    },
    formData: {
      type: Object,
      default: () => ({})
    },
    hideView: {
      type: Boolean,
      default: false
    },
    updateData: {
      type: Function,
      default: () => {}
    },
    fetchTabData: {
      type: Function,
      default: () => {}
    },
    replacePropertyValueByKey: {
      type: Function,
      default: () => {}
    }
  },

  data () {
    return {
      formDataCopy: {},
      showLoader: false,
      defaultPrependImageWidth: DEFAULT_PREPEND_IMAGE_WIDTH,
      defaultPrependImageHeight: DEFAULT_PREPEND_IMAGE_HEIGHT
    }
  },

  watch: {
    'config.externalData': {
      immediate: true,
      deep: true,
      handler (val) {
        if (val && !isEmpty(val) && isObject(val)) {
          this.formDataCopy = val
        }
      }
    },
    'config.showLoader': {
      immediate: true,
      handler (val) {
        this.showLoader = !!val
      }
    },
    formData: {
      immediate: true,
      deep: true,
      handler (val) {
        if (val && !isEmpty(val) && isObject(val)) {
          const { dataKey } = this.config || {}

          if (dataKey && val[dataKey]) {
            this.formDataCopy = val
          }
        }
      }
    }
  },

  methods: {
    getBlockStyle (block) {
      if (!block || !isObject(block)) {
        return {}
      }
      const { style } = block || {}

      return style && isObject(style) ? style : {}
    },

    canShowBlocks () {
      const { dataKey, visible } = this.config || {}

      return dataKey && typeof dataKey === 'string' && visible === true && !this.hideView
    },

    getBlockValue (block, dataIndex) {
      const { customValue, defaultValue, key } = block || {}
      const { dataKey } = this.config || {}
      let blockValue = null

      if (this.formDataCopy[dataKey] && this.formDataCopy[dataKey][dataIndex] && isObject(this.formDataCopy[dataKey][dataIndex])) {
        if (typeof customValue === 'function') {
          blockValue = customValue(this.formDataCopy[dataKey][dataIndex])
        }
        else {
          blockValue = this.formDataCopy[dataKey][dataIndex][key] ?? null
        }
      }

      return blockValue ?? defaultValue ?? DEFAULT_BLOCK_VALUE
    },

    getBlocksWrapperClasses () {
      const { wrapperClasses } = this.config || {}

      return wrapperClasses && typeof wrapperClasses === 'string' ? 'v-card-text ' + wrapperClasses : 'v-card-text'
    },

    onButtonClick (button) {
      const { click } = button || {}

      if (typeof click === 'function') {
        click({
          formData: this.formDataCopy,
          hideView: this.hideView,
          fetchTabData: () => this.fetchTabData(),
          updateData: (val) => this.updateData(val)
        })
      }
    },

    onBlockClick (block, blockIndex) {
      const { click, key } = block || {}
      const { dataKey } = this.config || {}

      if (typeof click === 'function') {
        click({
          formData: this.formDataCopy,
          index: blockIndex,
          hideView: this.hideView,
          rowData: this.formDataCopy[dataKey][blockIndex],
          data: this.formDataCopy[dataKey][blockIndex][key] ?? null,
          updateData: (val) => this.updateData(val)
        })
      }
    },

    getBlockClasses (block) {
      const { class: blockClass, click } = block || {}
      let classes = blockClass || ''

      if (typeof click === 'function') {
        classes += ' cursor-pointer'
      }

      return classes
    },

    canShowBlock (block, rowIndex) {
      const blockValue = this.getBlockValue(block, rowIndex)
      const { defaultValue, visible } = block || {}
      let show

      if (block.deletable === true) {
        return blockValue && blockValue !== DEFAULT_BLOCK_VALUE && blockValue !== defaultValue
      }
      if (typeof visible === 'function') {
        show = visible({
          blockValue
        })
      }

      return show === undefined || show === true
    },

    onSingleBlockDelete (block, rowIndex, blockIndex) {
      const { key } = block || {}
      const { dataKey, onRemoveBlock } = this.config || {}
      // Deep clone to avoid changing the original data
      const formDataCopy = cloneDeep(this.formDataCopy)

      if (typeof this.replacePropertyValueByKey === 'function' && dataKey && key && formDataCopy[dataKey] && formDataCopy[dataKey][rowIndex] && formDataCopy[dataKey][rowIndex][key]) {
        // If there is only one block left, remove the row
        if (Object.keys(formDataCopy[dataKey][rowIndex]).length === 1) {
          formDataCopy[dataKey].splice(rowIndex, 1)
        }
        else {
          delete formDataCopy[dataKey][rowIndex][key]
        }

        this.$delete(this.config.columnsConfig, blockIndex)
        if (typeof onRemoveBlock === 'function') {
          onRemoveBlock({
            block,
            blockIndex,
            rowIndex,
            formData: this.formDataCopy,
            updateData: (val) => this.updateData(val)
          })
        }
        // Apply new blocks of data to the main form data
        this.replacePropertyValueByKey(dataKey, formDataCopy[dataKey])
      }
    }
  }
}
</script>

<style scoped>
.title {
  font-family: 'Satoshi', sans-serif !important;
  font-size: 20px !important;
  font-weight: 500;
  color: #09090B;
  line-height: 28px;
  display: flex;
  justify-content: start;
  padding-top: 0 !important;
  padding-left: 0;
  padding-bottom: 10px;
  align-self: end;
}

.block-col {
  position: relative;
  background-color: #FAFAFA;
  margin-right: 5px;
  border-radius: 8px;
  padding: 16px;
  min-width: fit-content;
  flex-grow: unset;
  height: 84px;
  font-family: 'Satoshi', sans-serif;
  color: #71717A;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.blocks-row {
  flex-wrap: nowrap;
}

.blocks-row:not(:last-child) {
  margin-bottom: 16px;
}

.block-value {
  font-family: 'Satoshi', sans-serif;
  color: #09090B;
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
}

.no-data-text {
  font-family: 'Satoshi', sans-serif;
  color: #71717A;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.v-card {
  margin: 0 16px 14px 16px;
}

.cursor-pointer {
  cursor: pointer;
}

.delete-image {
  position: absolute;
  top: 0;
  right: 0;
}

.delete-image:hover {
  cursor: pointer;
}

.v-card-text {
  position: relative;
}
</style>
